import { styled } from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { colors } from "../../../theme/colors";
import { Card } from "../../../components/ui/card/card.component";
import { Spin, Table, Button } from "antd";
import { ProfessionalProfile } from "../../../models/professional-profile.model";
import { useCallback, useEffect, useRef, useState } from "react";
import { axios } from "../../../api/axios";
import { AxiosError } from "axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { Center } from "../../../components/layout/center/center.component";
import useModal from "antd/es/modal/useModal";
import { useNavigate, useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { masterAtom } from "../../../atoms/masters.atom";
import { valueToLabel } from "../../../utils/functions/misc/valueToLabel";
import {
  FacebookIcon,
  LinkedinIcon,
  WhatsappIcon,
  XIcon,
} from "react-share";
import { Pressable } from "../../../components/ui/pressable/pressable.component";
import _, { capitalize } from "lodash";
import { MdEmail, MdPhone, MdSchool, MdShare, MdWork } from "react-icons/md";
import dayjs from "dayjs";
import toast, { Toaster } from "react-hot-toast";
import { LinkingAlert } from "../../../models/linking-alert.model";
import { jsonToFormData } from "../../../utils/functions/misc/jsonToFormData";
import { sluggify } from "../../../utils/functions/string/sluggify";
import { wait } from "../../../utils/functions/misc/wait";

export const ViewProfessionalProfile = () => {
  const { customURL } = useParams();

  const master = useAtomValue(masterAtom);
  const auth = useAtomValue(authAtom);

  const logo = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  const [modal, modalConfig] = useModal();

  const [professionalProfile, setProfessionalProfile] = useState<
    | (ProfessionalProfile & {
        email?: string;
        phone_number?: string;
        name?: string;
      })
    | null
  >(null);
  const [loaded, setLoaded] = useState(false);
  const [alerts, setAlerts] = useState<LinkingAlert[]>([]);

  useEffect(() => {
    if (auth.authenticated)
      axios
        .get<{
          film_alerts: LinkingAlert[];
          open_tickets_count: number;
        }>("/client/alerts/me")
        .then((response) => {
          setAlerts(response.data.film_alerts);
        })
        .catch(handleResponse);
  }, [auth]);

  useEffect(() => {
    if (auth.authenticated && !customURL) {
      axios
        .get<{
          profile: ProfessionalProfile;
          qualifications: ProfessionalProfile["qualifications"];
          experiences: ProfessionalProfile["experiences"];
        }>("/auth/professional/profile")
        .then((result) => {
          setLoaded(true);
          setProfessionalProfile({
            ...result.data.profile,
            qualifications: result.data.qualifications,
            experiences: result.data.experiences,
          });
        })
        .catch((err) => {
          if (err instanceof AxiosError && err.response?.status === 404) {
            setLoaded(true);
          } else {
            handleResponse(err);
          }
        });
    } else if (customURL) {
      axios
        .get<{
          user_details?: { email: string; phone_number: string; name: string };
          profile: ProfessionalProfile;
          qualifications: ProfessionalProfile["qualifications"];
          experiences: ProfessionalProfile["experiences"];
        }>("/client/profile/professional", {
          headers: { "X-Profile-ID": customURL },
        })
        .then((result) => {
          setLoaded(true);
          setProfessionalProfile({
            ...result.data.profile,
            qualifications: result.data.qualifications,
            experiences: result.data.experiences,
            ...(result.data.user_details ?? {}),
          });
        })
        .catch((err) => {
          if (err instanceof AxiosError && err.response?.status === 404) {
            setLoaded(true);
          } else {
            handleResponse(err);
          }
        });
    } else {
      navigate("/");
    }
  }, [auth, customURL]);

  useEffect(() => {
    if (loaded && !professionalProfile) {
      modal.confirm({
        title: "Would you like to create a professional profile?",
        content:
          "You have not yet created a professional profile. Would you like to create one?",
        okText: "Create",
        onOk: () => {
          navigate("/p/create", { replace: true });
        },
        onCancel: () => {
          navigate("/");
        },
      });
    }
  }, [loaded, professionalProfile]);

  const updateLinkStatus = useCallback(
    (linkId: string, linkType: string, status: "verified" | "declined") => {
      axios
        .post(
          "/client/update/requests",
          jsonToFormData({
            linking_request_id: linkId,
            status,
            linking_type: sluggify(linkType, "_"),
          })
        )
        .then((resp) => {
          handleResponse(resp);
          wait(500).then(() => window.location.reload());
        })
        .catch(handleResponse);
    },
    []
  );

  const onShare = async () => {
    if (!professionalProfile) return;
    const url = `https://distribute.pocketfilms.in/${professionalProfile.custom_username}`;
    let profilePic: File | null = null;
    if (professionalProfile.logo) {
      try {
        const resp = await fetch(professionalProfile.logo);
        const blob = await resp.blob();
        profilePic = new File(
          [blob],
          `${
            (customURL ? professionalProfile.name : auth.user?.full_name) ?? ""
          }_Profile_Picture`
        );
      } catch (e) {}
    }

    if (
      (window.navigator as any).share &&
      window.navigator.canShare({ url })
    ) {
      try {
        await navigator.share({
          url,
          files: profilePic ? [profilePic] : [],
        });
      } catch (e) {}
    } else {
      toast.success("Copied to clipboard!");
      window.navigator.clipboard.writeText(url);
    }
  };

  return (
    <Page>
      {customURL ? (
        <Toaster
          toastOptions={{
            style: {
              fontSize: "1.3rem",
            },
          }}
        />
      ) : null}
      {modalConfig}
      <BackButton />

      {loaded && professionalProfile ? (
        <Container>
          <HeaderCard>
            <HeaderTop>
              <Logo
                ref={logo}
                style={{
                  backgroundImage: `url(${
                    professionalProfile.logo ?? "/images/ui/avatar.png"
                  })`,
                }}
                onError={() => {
                  if (logo.current)
                    logo.current.style.backgroundImage =
                      "url(/images/ui/avatar.png)";
                }}
              />
              <HeaderInfo>
                <Name>
                  {customURL
                    ? professionalProfile.name
                    : `${auth.user?.first_name ?? ""} ${
                        auth.user?.last_name ?? ""
                      }`}
                </Name>
                <Skills>
                  {(professionalProfile.skills ?? [])
                    .map((skill) => valueToLabel("skill", skill, master))
                    .filter(Boolean)
                    .join(" · ")}
                </Skills>
              </HeaderInfo>
              <HeaderActions>
                {!customURL ? (
                  <ActionButton onClick={() => navigate("/p/edit")}>
                    <EditOutlined />
                    <span>Edit</span>
                  </ActionButton>
                ) : null}
                <ActionButton $primary onClick={onShare}>
                  <MdShare size={16} />
                  <span>Share</span>
                </ActionButton>
              </HeaderActions>
            </HeaderTop>
            {professionalProfile.about ? (
              <About>{professionalProfile.about}</About>
            ) : null}
          </HeaderCard>

          <CardGrid>
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Contact</SectionTitle>
              </SectionHeader>
              <List>
                {(() => {
                  const email = customURL
                    ? professionalProfile.email
                    : auth.user?.email;
                  return email ? (
                    <ListRow>
                      <IconCircle>
                        <MdEmail size={16} />
                      </IconCircle>
                      <ListText>
                        <ListLabel>Email</ListLabel>
                        <LinkText
                          onClick={() => window.open(`mailto:${email}`)}
                        >
                          {email}
                        </LinkText>
                      </ListText>
                    </ListRow>
                  ) : null;
                })()}
                {(() => {
                  const phone = customURL
                    ? professionalProfile.phone_number
                    : auth.user?.phone_code && auth.user?.phone_number
                    ? `+${auth.user.phone_code}${auth.user.phone_number}`
                    : null;
                  return phone ? (
                    <ListRow>
                      <IconCircle>
                        <MdPhone size={16} />
                      </IconCircle>
                      <ListText>
                        <ListLabel>Phone</ListLabel>
                        <LinkText onClick={() => window.open(`tel:${phone}`)}>
                          {phone}
                        </LinkText>
                      </ListText>
                    </ListRow>
                  ) : null;
                })()}
                <SocialRow>
                  {professionalProfile.facebook_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(professionalProfile.facebook_url)
                      }
                    >
                      <FacebookIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {professionalProfile.linkedin_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(professionalProfile.linkedin_url)
                      }
                    >
                      <LinkedinIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {professionalProfile.x_url ? (
                    <SocialLink
                      onClick={() => window.open(professionalProfile.x_url)}
                    >
                      <XIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {professionalProfile.whatsapp_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(professionalProfile.whatsapp_url)
                      }
                    >
                      <WhatsappIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {professionalProfile.instagram_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(professionalProfile.instagram_url)
                      }
                    >
                      <InstagramImg src="/images/ui/instagram.png" />
                    </SocialLink>
                  ) : null}
                </SocialRow>
              </List>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitle>Qualifications</SectionTitle>
              </SectionHeader>
              <List>
                {(professionalProfile.qualifications ?? []).length === 0 ? (
                  <Empty>No qualifications added.</Empty>
                ) : (
                  (professionalProfile.qualifications ?? []).map(
                    (qualification, idx) => (
                      <ListRow key={`q-${idx}`}>
                        <IconCircle>
                          <MdSchool size={16} />
                        </IconCircle>
                        <ListText>
                          <ItemTitle>{qualification.degree}</ItemTitle>
                          <ItemSub>
                            {qualification.institute} ·{" "}
                            {qualification.year.toString()}
                          </ItemSub>
                        </ListText>
                      </ListRow>
                    )
                  )
                )}
              </List>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitle>Work Experience</SectionTitle>
              </SectionHeader>
              <List>
                {(professionalProfile.experiences ?? []).length === 0 ? (
                  <Empty>No work experience added.</Empty>
                ) : (
                  (professionalProfile.experiences ?? []).map(
                    (experience, idx) => (
                      <ListRow key={`e-${idx}`}>
                        <IconCircle>
                          <MdWork size={16} />
                        </IconCircle>
                        <ListText>
                          <ItemTitle>
                            {valueToLabel(
                              "company_role",
                              experience.position,
                              master
                            )}{" "}
                            ({capitalize(experience.work_type)}) ·{" "}
                            {experience.company}
                          </ItemTitle>
                          <ItemSub>
                            {dayjs(experience.start_date, "DD-MM-YYYY").format(
                              "MMM 'YY"
                            )}{" "}
                            -{" "}
                            {experience.is_current
                              ? "Present"
                              : dayjs(
                                  experience.end_date,
                                  "DD-MM-YYYY"
                                ).format("MMM 'YY")}
                          </ItemSub>
                        </ListText>
                      </ListRow>
                    )
                  )
                )}
              </List>
            </SectionCard>
          </CardGrid>

          {!customURL && alerts.length > 0 ? (
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Linking Requests</SectionTitle>
                <SectionHint>
                  Requests from filmmakers to link your profile to a project.
                </SectionHint>
              </SectionHeader>
              <Table
                pagination={false}
                columns={[
                  {
                    title: "Film",
                    key: "film_title",
                    render: (_, alert) => alert.film_title,
                  },
                  {
                    title: "Role",
                    key: "role",
                    render: (__, alert) => _.capitalize(alert.role),
                  },
                  {
                    title: "Status",
                    key: "status",
                    render: (__, alert) => _.capitalize(alert.status),
                  },
                  {
                    title: "",
                    key: "actions",
                    width: 250,
                    align: "right",
                    render: (_, alert) => {
                      if (alert.status === "pending") {
                        return (
                          <TableActions>
                            <Button
                              size="middle"
                              onClick={() =>
                                updateLinkStatus(
                                  alert.id,
                                  alert.type,
                                  "declined"
                                )
                              }
                            >
                              Reject
                            </Button>
                            <Button
                              type="primary"
                              size="middle"
                              onClick={() =>
                                updateLinkStatus(
                                  alert.id,
                                  alert.type,
                                  "verified"
                                )
                              }
                            >
                              Approve
                            </Button>
                          </TableActions>
                        );
                      }
                    },
                  },
                ]}
                dataSource={alerts}
                rowKey="id"
              />
            </SectionCard>
          ) : null}
        </Container>
      ) : (
        <LoadingWrap>
          <Center>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </Center>
        </LoadingWrap>
      )}
    </Page>
  );
};

const Page = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: ${colors.lightBackground.hex};
  padding: 8rem 2rem 6rem;
  position: relative;

  @media (max-width: 36em) {
    padding: 7rem 1.2rem 5rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 120rem;
  margin: 0 auto;
  width: 100%;
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;

  @media (max-width: 36em) {
    padding: 1.6rem 1.6rem;
  }
`;

const HeaderCard = styled(SectionCard)`
  background: linear-gradient(
    135deg,
    ${colors.primary.withOpacity(0.06).hex} 0%,
    ${colors.background.hex} 60%
  );
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  width: 100%;
  flex-wrap: wrap;
`;

const Logo = styled.div`
  width: 11rem;
  height: 11rem;
  border-radius: 1rem;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-color: ${colors.grey.hex};
  border: 1.5px solid ${colors.primary.withOpacity(0.2).hex};
  flex-shrink: 0;

  @media (max-width: 36em) {
    width: 8rem;
    height: 8rem;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
  min-width: 18rem;
`;

const Name = styled.h1`
  font-size: 2.8rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  line-height: 1.2;

  @media (max-width: 36em) {
    font-size: 2.2rem;
  }
`;

const Skills = styled.span`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.65).hex};
  line-height: 1.4;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const ActionButton = styled(Pressable)<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.6rem;
  padding: 0 1.4rem;
  border-radius: 0.8rem;
  font-size: 1.3rem;
  font-weight: 600;
  border: 1.5px solid
    ${({ $primary }) =>
      $primary ? colors.primary.hex : colors.primary.withOpacity(0.3).hex};
  background: ${({ $primary }) =>
    $primary ? colors.primary.hex : "transparent"};
  color: ${({ $primary }) =>
    $primary ? colors.white.hex : colors.text.hex};
  transition: all 0.15s;

  &:hover {
    background: ${({ $primary }) =>
      $primary
        ? colors.primary.withOpacity(0.9).hex
        : colors.primary.withOpacity(0.08).hex};
  }
`;

const About = styled.p`
  margin: 1.6rem 0 0;
  font-size: 1.4rem;
  line-height: 1.6;
  color: ${colors.text.withOpacity(0.85).hex};
  white-space: pre-wrap;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2rem;
  width: 100%;

  @media (max-width: 75em) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: 50em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const SectionHint = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.4;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const ListRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: ${colors.primary.withOpacity(0.1).hex};
  color: ${colors.primary.hex};
  flex-shrink: 0;
`;

const ListText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`;

const ListLabel = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const ItemTitle = styled.span`
  font-size: 1.4rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.3;
`;

const ItemSub = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.3;
`;

const LinkText = styled.span`
  font-size: 1.4rem;
  color: ${colors.primary.hex};
  cursor: pointer;
  word-break: break-all;
  line-height: 1.3;

  &:hover {
    text-decoration: underline;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.4rem;
`;

const SocialLink = styled(Pressable)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  overflow: hidden;
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-2px);
  }

  & > * {
    border-radius: 50%;
  }
`;

const InstagramImg = styled.img`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
`;

const Empty = styled.span`
  font-size: 1.3rem;
  color: ${colors.text.withOpacity(0.5).hex};
`;

const TableActions = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: flex-end;
`;

const LoadingWrap = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
